import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeft, FileSpreadsheet, Plus, Printer, RotateCcw, Save, Search, Trash2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { EmptyState, ErrorState } from '../components/ui/feedback-state'
import { api, getApiErrorMessage } from '../lib/api'
import {
  buildCoverageRemodelingReport,

  coverageRemodelingDifference,
  coverageRemodelingRowTotal,
  coverageSimulationCurrentRowTotal,
  coverageSimulationPlanKey,
  createCoverageRemodelingPlan,
  formatCoverageSimulationAmount,
  normalizeCoverageRemodelingSimulation,
  positiveCoverageAmount,
} from '../lib/coverageRemodelingSimulation'
import { buildCoverageTreatmentStage } from '../lib/coverageTreatmentStages'

const GROUP_COLORS = {
  사망: ['#64748b', '#f1f5f9'],
  장해: ['#7c6aa6', '#f4f1fb'],
  암: ['#c85d45', '#fff1ed'],
  뇌심: ['#4f7f72', '#edf8f4'],
  '뇌/심': ['#4f7f72', '#edf8f4'],
  뇌: ['#4f7f72', '#edf8f4'],
  심장: ['#4777a3', '#eef6fc'],
  수술비: ['#327a99', '#edf7fa'],
  입원일당: ['#a27d25', '#fcf7e9'],
  실손: ['#26826c', '#ecf8f4'],
  운전자: ['#7c5f91', '#f7f1fa'],
  간병: ['#596da5', '#eef1fb'],
  '치매/간병': ['#596da5', '#eef1fb'],
  연금: ['#277e93', '#edf9fb'],
  치아: ['#a86c3b', '#fff5eb'],
  배상: ['#477e8c', '#edf7f9'],
  기타: ['#6b7280', '#f5f6f8'],
}

function unwrap(res) {
  const body = res?.data
  return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
}

function safeFilenamePart(value, fallback = '고객') {
  const text = String(value || '').trim().replace(/[\\/:*?"<>|]/g, '_')
  return text || fallback
}

function filenameFromDisposition(headers, fallback) {
  const value = headers?.['content-disposition'] || ''
  const encoded = value.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  if (encoded) {
    try { return decodeURIComponent(encoded) } catch { return fallback }
  }
  return value.match(/filename="?([^";]+)"?/i)?.[1] || fallback
}

function groupColors(group) {
  const value = String(group || '')
  const key = Object.keys(GROUP_COLORS).find((candidate) => value.includes(candidate))
  return GROUP_COLORS[key] || ['#64748b', '#f8fafc']
}

function isOtherRow(row) {
  return String(row?.coverageGroup || '').trim() === '기타'
}

const SUMMARY_CARD_DEFINITIONS = [
  { group: '사망보장', items: [{ label: '상해사망', tokens: ['상해사망'] }, { label: '질병사망', tokens: ['질병사망'] }] },
  { group: '장해', items: [{ label: '상해후유장해(3~100%)', tokens: ['상해후유장해(3~100%)', '상해후유장해3100'] }, { label: '질병후유장해(3~100%)', tokens: ['질병후유장해(3~100%)', '질병후유장해3100'] }, { label: '상해후유장해(80% 이상)', tokens: ['상해후유장해80', '상해80%후유장해'] }, { label: '질병후유장해(80% 이상)', tokens: ['질병후유장해80', '질병80%후유장해'] }] },
  { group: '암 진단비', items: [{ label: '일반암진단비', tokens: ['일반암'] }, { label: '유사암진단비', tokens: ['유사암'] }] },
  { group: '뇌혈관 진단비', items: [{ label: '뇌혈관질환진단비', tokens: ['뇌혈관'] }, { label: '뇌졸중진단비', tokens: ['뇌졸중'] }, { label: '뇌출혈진단비', tokens: ['뇌출혈'] }] },
  { group: '심장 진단비', items: [{ label: '특정심혈관질환진단비', tokens: ['특정심혈관'] }, { label: '허혈성심장질환진단비', tokens: ['허혈성심장'] }, { label: '급성심근경색진단비', tokens: ['급성심근경색'] }] },
  { group: '질병수술비', items: [{ label: '질병수술비', tokens: ['질병수술비'] }, { label: '질병 종 수술비', tokens: ['질병종수술비'] }] },
  { group: '상해수술비', items: [{ label: '상해수술비', tokens: ['상해수술비'] }, { label: '상해 종 수술비', tokens: ['상해종수술비'] }] },
  { group: 'N대 수술비', items: [{ label: 'N대 질병수술비', tokens: ['n대질병수술비', 'n대수술비'] }] },
  { group: '입원일당', items: [{ label: '질병입원일당', tokens: ['질병입원일당', '질병입원'] }, { label: '상해입원일당', tokens: ['상해입원일당', '상해입원'] }, { label: '중환자실 일당', tokens: ['중환자실'] }] },
  { group: '운전자', items: [{ label: '교통사고처리지원금', tokens: ['교통사고처리지원금'] }, { label: '변호사선임비용', tokens: ['변호사선임'] }, { label: '운전자교통사고벌금', tokens: ['운전자교통사고벌금', '교통사고벌금'] }] },
  { group: '실손의료비', items: [{ label: '상해 입원', tokens: ['상해입원'] }, { label: '상해 통원', tokens: ['상해통원'] }, { label: '질병 입원', tokens: ['질병입원'] }, { label: '질병 통원', tokens: ['질병통원'] }] },
  { group: '간병인보험', items: [{ label: '간병인/간호간병 상해일당', tokens: ['간병인상해', '간호간병상해'] }, { label: '간병인/간호간병 질병일당', tokens: ['간병인질병', '간호간병질병'] }] },
  { group: '연금', items: [{ label: '연금(월보험료)', groups: ['연금'], tokens: ['연금'] }] },
  { group: '치아보험', items: [{ label: '보철치료', tokens: ['보철'] }, { label: '보존치료', tokens: ['보존'] }] },
  { group: '배상책임', items: [{ label: '일상생활배상책임', tokens: ['일상생활배상', '일배책'] }, { label: '가족배상책임', tokens: ['가족배상', '가족일상생활배상'] }] },
]

function normalizedCoverageText(value) {
  return String(value || '').replace(/[\s·ㆍ/()-]/g, '').toLowerCase()
}

function summaryCoverageItem(rows, item, index) {
  const tokens = Array.isArray(item.tokens) ? item.tokens.map(normalizedCoverageText).filter(Boolean) : []
  const groups = Array.isArray(item.groups) ? item.groups : []
  let matched = tokens.length
    ? rows.find((row) => tokens.some((token) => normalizedCoverageText(row?.coverageName).includes(token)))
    : null
  if (!matched && groups.length) matched = rows.find((row) => groups.includes(String(row?.coverageGroup || '').trim()))
  return {
    key: matched?._simulationKey || `${item.label}-${index}`,
    label: item.label,
    amount: positiveCoverageAmount(matched?._beforeAmount),
  }
}

function buildSummaryCards(rows) {
  return SUMMARY_CARD_DEFINITIONS.map(({ group, items }) => ({
    group,
    items: items.map((item, index) => summaryCoverageItem(rows, item, index)),
  }))
}

function formatWon(value) {
  const amount = positiveCoverageAmount(value)
  return amount ? `${amount.toLocaleString('ko-KR')}원` : '-'
}

function customerAge(birthDate) {
  const match = String(birthDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return '-'
  const today = new Date()
  let age = today.getFullYear() - Number(match[1])
  const birthdayPassed = today.getMonth() + 1 > Number(match[2])
    || (today.getMonth() + 1 === Number(match[2]) && today.getDate() >= Number(match[3]))
  if (!birthdayPassed) age -= 1
  return age >= 0 ? `${age}세` : '-'
}

function collectCurrentProducts(report) {
  const products = new Map()
  ;(Array.isArray(report?.rows) ? report.rows : []).forEach((row, rowIndex) => {
    ;(Array.isArray(row?.products) ? row.products : []).forEach((product, productIndex) => {
      const key = coverageSimulationPlanKey(product, `CURRENT_${rowIndex}_${productIndex}`)
      if (!products.has(key)) products.set(key, { ...product, productKey: key })
    })
  })
  ;(Array.isArray(report?.contracts) ? report.contracts : []).forEach((contract, index) => {
    const matching = Array.from(products.values()).find((product) => (
      positiveCoverageAmount(product.contractId) > 0
      && positiveCoverageAmount(product.contractId) === positiveCoverageAmount(contract.id)
    ))
    const key = matching?.productKey || coverageSimulationPlanKey(contract, `CONTRACT_${contract.id || index}`)
    products.set(key, { ...(products.get(key) || {}), ...contract, productKey: key })
  })
  return Array.from(products.values())
}

function currentProductForRow(row, product) {
  const products = Array.isArray(row?.currentProducts) ? row.currentProducts : []
  return products.find((item) => coverageSimulationPlanKey(item) === product.productKey)
    || products.find((item) => positiveCoverageAmount(item?.contractId) > 0
      && positiveCoverageAmount(item.contractId) === positiveCoverageAmount(product?.contractId || product?.id))
}

function currentProductAmount(row, product) {
  return positiveCoverageAmount(currentProductForRow(row, product)?.amount)
}

function currentProductModified(row, product) {
  const currentProduct = currentProductForRow(row, product)
  return Boolean(currentProduct)
    && positiveCoverageAmount(currentProduct.amount) !== positiveCoverageAmount(currentProduct._initialAmount)
}

function hasCurrentCoverageChange(row) {
  return (Array.isArray(row?.currentProducts) ? row.currentProducts : [])
    .some((product) => positiveCoverageAmount(product?.amount) !== positiveCoverageAmount(product?._initialAmount))
}
function ToolbarButton({ children, tone = '', ...props }) {
  return <button type="button" className={`rd-tool-button ${tone}`} {...props}>{children}</button>
}

function AmountInput({ value, onChange, label, testId }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      aria-label={label}
      data-testid={testId}
      className="rd-amount-input"
      value={positiveCoverageAmount(value) ? positiveCoverageAmount(value).toLocaleString('ko-KR') : ''}
      placeholder="-"
      onFocus={(event) => event.currentTarget.select()}
      onChange={(event) => onChange(positiveCoverageAmount(String(event.target.value).replace(/[^0-9]/g, '')))}
    />
  )
}

function MetricCell({ label, value, detail = '', tone = '' }) {
  return (
    <div className={`rd-metric ${tone}`}>
      <div className="rd-metric-label">{label}</div>
      <div className="rd-metric-value">{value}</div>
      {detail ? <div className="rd-metric-detail">{detail}</div> : null}
    </div>
  )
}

function CoverageMetric({ group, items }) {
  const [band] = groupColors(group)
  return (
    <div className="rd-metric">
      <div className="rd-metric-label rd-metric-heading"><span className="rd-dot" style={{ backgroundColor: band }} />{group}</div>
      <div className="rd-metric-list">
        {items.map((item) => (
          <div key={item.key} className="rd-metric-row">
            <span title={item.label}>{item.label}</span>
            <strong className={positiveCoverageAmount(item.amount) ? '' : 'is-empty'}>{positiveCoverageAmount(item.amount) ? formatCoverageSimulationAmount(item.amount) : '미가입'}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function CustomerBoard({ report, rows, currentPremium, proposedPremium, plans, summaryCollapsed, onToggleSummary }) {
  const customer = report?.customer || {}
  const summaryCards = useMemo(() => buildSummaryCards(rows), [rows])

  if (summaryCollapsed) {
    return (
      <div className="rd-board rd-board-collapsed">
        <section className="rd-summary-collapsed rd-no-print" aria-label="보장 분석 요약">
          <button type="button" className="rd-summary-toggle" onClick={onToggleSummary}>요약 펼치기</button>
        </section>
      </div>
    )
  }

  return (
    <div className="rd-board">
      <section className="rd-card rd-customer" aria-label="고객 정보">
        <div className="rd-eyebrow">고객 정보</div>
        <div className="rd-customer-grid">
          <label className="full"><span>이름</span><input readOnly value={customer.customerName || '-'} /></label>
          <label><span>나이</span><input readOnly value={customerAge(customer.birthDate)} /></label>
          <label><span>성별</span><input readOnly value={customer.gender === 'MALE' ? '남성' : customer.gender === 'FEMALE' ? '여성' : customer.gender || '-'} /></label>
          <label className="full"><span>직업</span><input readOnly value={customer.jobName || customer.occupationName || '-'} /></label>
        </div>
      </section>
      <section className="rd-summary" aria-label="보장 분석 요약">
        <div className="rd-summary-tools rd-no-print">
          <button type="button" className="rd-summary-toggle" onClick={onToggleSummary}>요약 접기</button>
        </div>
        <div className="rd-summary-grid">
          <MetricCell label="현재 월 보험료" value={formatWon(currentPremium)} detail={`계약 ${report?.contractCount ?? report?.contracts?.length ?? 0}건`} tone="gold" />
          <MetricCell label="리모델링 월 보험료" value={formatWon(proposedPremium)} detail={`제안 ${plans.length}건`} tone="blue" />
          {summaryCards.map(({ group, items }) => <CoverageMetric key={group} group={group} items={items} />)}
        </div>
      </section>
    </div>
  )
}
function CurrentContractHeader({ product }) {
  return (
    <div className="rd-contract-head">
      <span className="rd-contract-badge">기계약</span>
      <strong>{product.insurerName || '-'}</strong>
      <div className="rd-product-name" title={product.productName || ''}>{product.productName || '-'}</div>
      <div className="rd-contract-meta">{product.contractDate || '-'} · {product.paymentPeriod || '-'}</div>
      <div className="rd-contract-meta">{product.coveragePeriod || '-'}</div>
      <div className="rd-premium">{formatWon(product.monthlyPremium)} <span>/월</span></div>
    </div>
  )
}

function ProposedContractHeader({ plan, planIndex, setPlanField, removePlan }) {
  return (
    <div className="rd-contract-head rd-proposed-head">
      <div className="rd-contract-head-line">
        <span className="rd-contract-badge is-new">신규제안</span>
        <button type="button" className="rd-delete" aria-label={`${plan.productName || `추천 플랜 ${planIndex + 1}`} 삭제`} title="제안 플랜 삭제" onClick={() => removePlan(planIndex)}><Trash2 /></button>
      </div>
      <input data-testid={`remodeling-plan-insurer-${planIndex}`} aria-label={`추천 플랜 ${planIndex + 1} 보험회사`} value={plan.insurerName || ''} onChange={(event) => setPlanField(planIndex, 'insurerName', event.target.value)} placeholder="보험회사" />
      <input data-testid={`remodeling-plan-name-${planIndex}`} aria-label={`추천 플랜 ${planIndex + 1} 상품명`} value={plan.productName || ''} onChange={(event) => setPlanField(planIndex, 'productName', event.target.value)} placeholder="상품명" />
      <div className="rd-contract-input-row">
        <input aria-label={`추천 플랜 ${planIndex + 1} 가입일`} value={plan.contractDate || ''} onChange={(event) => setPlanField(planIndex, 'contractDate', event.target.value)} placeholder="가입일" />
        <input aria-label={`추천 플랜 ${planIndex + 1} 납입기간`} value={plan.paymentPeriod || ''} onChange={(event) => setPlanField(planIndex, 'paymentPeriod', event.target.value)} placeholder="납입기간" />
      </div>
      <input aria-label={`추천 플랜 ${planIndex + 1} 만기`} value={plan.coveragePeriod || ''} onChange={(event) => setPlanField(planIndex, 'coveragePeriod', event.target.value)} placeholder="만기/보장기간" />
      <div className="rd-premium-input"><input data-testid={`remodeling-plan-premium-${planIndex}`} aria-label={`추천 플랜 ${planIndex + 1} 월 보험료`} inputMode="numeric" value={positiveCoverageAmount(plan.monthlyPremium) ? positiveCoverageAmount(plan.monthlyPremium).toLocaleString('ko-KR') : ''} onChange={(event) => setPlanField(planIndex, 'monthlyPremium', positiveCoverageAmount(String(event.target.value).replace(/[^0-9]/g, '')))} placeholder="월 보험료" /><span>원/월</span></div>
    </div>
  )
}

function PortfolioTable({ rows, currentProducts, plans, groupSpans, editCurrent, setPlanField, setCurrentRowAmount, setRowAmount, removePlan }) {
  if (!rows.length) return <EmptyState title="표시할 보장 항목이 없습니다" className="py-16" />
  return (
    <div className="rd-table-scroller">
      <table className="rd-portfolio-table">
        <thead>
          <tr>
            <th className="rd-cat rd-sticky-cat">분류</th>
            <th className="rd-name rd-sticky-name">담보 항목</th>
            {currentProducts.map((product) => <th key={product.productKey} className="rd-contract-column"><CurrentContractHeader product={product} /></th>)}
            {plans.map((plan, planIndex) => <th key={plan.productKey} className="rd-contract-column rd-new-column"><ProposedContractHeader plan={plan} planIndex={planIndex} setPlanField={setPlanField} removePlan={removePlan} /></th>)}
            <th className="rd-total-column rd-current-total">현재 합계</th>
            <th className="rd-total-column rd-proposed-total">리모델링 합계</th>
            <th className="rd-difference-column rd-difference">증감</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const span = groupSpans.get(rowIndex) || 0
            const [band, soft] = groupColors(row.coverageGroup)
            const after = coverageRemodelingRowTotal(row)
            const difference = coverageRemodelingDifference(row)
            const other = isOtherRow(row)
            const groupEnding = rows[rowIndex + 1]?.coverageGroup !== row.coverageGroup
            return (
              <tr key={row._simulationKey} className={groupEnding ? 'rd-group-ending' : ''}>
                {span ? <td rowSpan={span} className="rd-cat rd-sticky-cat rd-category-band" style={{ backgroundColor: band }}>{row.coverageGroup || '-'}</td> : null}
                <td className="rd-name rd-sticky-name rd-coverage-name" style={{ backgroundColor: soft }}>{row.coverageName || '-'}</td>
                {currentProducts.map((product) => {
                  const modified = currentProductModified(row, product)
                  return (
                    <td key={`${row._simulationKey}-${product.productKey}`} className={`rd-current-value ${editCurrent ? 'is-editing' : ''} ${modified ? 'is-modified' : ''}`}>
                      {editCurrent
                        ? <AmountInput value={currentProductAmount(row, product)} label={`${product.productName || product.insurerName || '기계약'} ${row.coverageName} 보장금액`} testId={`current-coverage-amount-${row.coverageCode || rowIndex}-${product.productKey}`} onChange={(amount) => setCurrentRowAmount(row._simulationKey, product, amount)} />
                        : formatCoverageSimulationAmount(currentProductAmount(row, product))}
                    </td>
                  )
                })}                {plans.map((plan, planIndex) => {
                  const product = (Array.isArray(row.products) ? row.products : []).find((item) => item.productKey === plan.productKey)
                  return <td key={`${row._simulationKey}-${plan.productKey}`} className="rd-new-value"><AmountInput value={product?.amount} label={`${row.coverageName} ${plan.productName || `추천 플랜 ${planIndex + 1}`} 보장금액`} testId={`remodeling-amount-${row.coverageCode || rowIndex}-${planIndex}`} onChange={(amount) => setRowAmount(row._simulationKey, plan.productKey, amount)} /></td>
                })}
                <td className="rd-total-column rd-current-total">{other ? '상품별 확인' : formatCoverageSimulationAmount(row._beforeAmount)}</td>
                <td className="rd-total-column rd-proposed-total">{other ? '상품별 확인' : formatCoverageSimulationAmount(after)}</td>
                <td className={`rd-difference-column rd-difference ${difference > 0 ? 'up' : difference < 0 ? 'down' : ''}`}>{other || difference === 0 ? '-' : `${difference > 0 ? '+' : '-'}${formatCoverageSimulationAmount(Math.abs(difference))}`}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function TreatmentStageTable({ kind, rows, hasRemodelingPlans, customerName }) {
  const stage = useMemo(() => buildCoverageTreatmentStage(kind, rows, hasRemodelingPlans), [hasRemodelingPlans, kind, rows])
  const groupHeaders = useMemo(() => {
    const groups = []
    stage.columns.forEach((column) => {
      const last = groups[groups.length - 1]
      if (last?.name === column.group) last.count += 1
      else groups.push({ name: column.group, count: 1, tone: column.tone })
    })
    return groups
  }, [stage.columns])
  return (
    <section className="rd-card rd-stage-card" data-testid={`treatment-stage-${kind}`}>
      <div className="rd-stage-head">
        <div><h2>{stage.title} · {customerName}님</h2><p>{hasRemodelingPlans ? '리모델링 제안 금액 기준' : '기계약 금액 기준'}</p></div>
        <span className="rd-linked-legend"><span />기계약 분석 자동 연동</span>
      </div>
      <div className="rd-stage-scroller">
        <table className="rd-stage-table">
          <thead>
            <tr>
              <th rowSpan={2} className="rd-stage-row-name">담보 / 치료단계</th>
              {groupHeaders.map((group) => <th key={group.name} colSpan={group.count} className={`rd-stage-group ${group.tone}`}>{group.name}</th>)}
            </tr>
            <tr>{stage.columns.map((column) => <th key={column.key} className={`rd-stage-column ${column.tone}`}>{column.label}</th>)}</tr>
          </thead>
          <tbody>
            {stage.rows.map((row) => (
              <tr key={row.codes[0]}>
                <td className="rd-stage-row-name"><strong>{row.source?.coverageName || row.label}</strong></td>
                {row.cells.map((cell, columnIndex) => (
                  <td key={cell.key} data-testid={`treatment-stage-${kind}-${row.codes[0]}-${columnIndex}`} className={`rd-stage-value ${cell.linked ? 'linked' : ''}`}>
                    {cell.linked ? <><strong>{formatCoverageSimulationAmount(cell.amount)}</strong>{hasRemodelingPlans && row.beforeAmount !== row.activeAmount ? <span>기존 {formatCoverageSimulationAmount(row.beforeAmount)}</span> : null}</> : null}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot><tr><td className="rd-stage-row-name">합계 (단계별 총 수령액)</td>{stage.totals.map((total, index) => <td key={stage.columns[index].key}>{formatCoverageSimulationAmount(total)}</td>)}</tr></tfoot>
        </table>
      </div>
      <div className="rd-stage-note">자동 연동 셀은 기계약 분석의 담보 금액을 사용합니다. 동일 담보가 여러 치료단계에 표시될 수 있으므로 단계 간 금액을 합산하지 않습니다.</div>
    </section>
  )
}

export default function CoverageRemodelingSimulationDashboard() {
  const { id: idParam } = useParams()
  const navigate = useNavigate()
  const customerId = Number(String(idParam || '').trim())
  const [report, setReport] = useState(null)
  const [plans, setPlans] = useState([])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [dirty, setDirty] = useState(false)
  const [activeTab, setActiveTab] = useState('analysis')
  const [query, setQuery] = useState('')
  const [groupFilter, setGroupFilter] = useState('')
  const [changedOnly, setChangedOnly] = useState(false)
  const [editCurrent, setEditCurrent] = useState(false)
  const [summaryCollapsed, setSummaryCollapsed] = useState(false)

  const applyReport = useCallback((nextReport) => {
    const simulation = normalizeCoverageRemodelingSimulation(nextReport)
    setReport(nextReport)
    setPlans(simulation.plans)
    setRows(simulation.rows)
    setDirty(false)
  }, [])

  const load = useCallback(async ({ showLoading = true, showNotice = false } = {}) => {
    if (!Number.isFinite(customerId) || customerId <= 0) return
    if (showLoading) setLoading(true)
    else setBusy('load')
    setError('')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary`, { __silent: true, __suppressErrorPageRedirect: true })
      applyReport(unwrap(res))
      if (showNotice) setNotice('저장된 리모델링 시뮬레이션을 불러왔습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '보장요약 리포트를 불러오지 못했습니다.'))
    } finally {
      if (showLoading) setLoading(false)
      else setBusy('')
    }
  }, [applyReport, customerId])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const onBeforeUnload = (event) => {
      if (!dirty) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const customerName = report?.customer?.customerName || '고객'
  const currentProducts = useMemo(() => collectCurrentProducts(report), [report])
  const groups = useMemo(() => Array.from(new Set(rows.map((row) => row.coverageGroup).filter(Boolean))), [rows])
  const visibleRows = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    return rows.filter((row) => {
      if (row.visible === false) return false
      if (groupFilter && row.coverageGroup !== groupFilter) return false
      if (changedOnly && coverageRemodelingDifference(row) === 0 && !hasCurrentCoverageChange(row)) return false
      if (!keyword) return true
      return `${row.coverageGroup || ''} ${row.coverageName || ''}`.toLowerCase().includes(keyword)
    })
  }, [changedOnly, groupFilter, query, rows])
  const groupSpans = useMemo(() => {
    const spans = new Map()
    let index = 0
    while (index < visibleRows.length) {
      const group = visibleRows[index]?.coverageGroup || ''
      let end = index + 1
      while (end < visibleRows.length && (visibleRows[end]?.coverageGroup || '') === group) end += 1
      spans.set(index, end - index)
      index = end
    }
    return spans
  }, [visibleRows])
  const currentPremium = useMemo(() => currentProducts.reduce((sum, product) => sum + positiveCoverageAmount(product.monthlyPremium), 0), [currentProducts])
  const proposedPremium = useMemo(() => {
    const planPremium = plans.reduce((sum, plan) => sum + positiveCoverageAmount(plan.monthlyPremium), 0)
    const replacesCurrentPortfolio = plans.some((plan) => String(plan?.planMode || '').toUpperCase() === 'REPLACE')
    return replacesCurrentPortfolio ? planPremium : currentPremium + planPremium
  }, [currentPremium, plans])

  const setPlanField = (planIndex, field, value) => {
    setPlans((prev) => prev.map((plan, index) => (index === planIndex ? { ...plan, [field]: value } : plan)))
    setDirty(true)
  }
  const setRowAmount = (rowKey, planKey, amount) => {
    setRows((prev) => prev.map((row) => {
      if (row._simulationKey !== rowKey) return row
      const products = Array.isArray(row.products) ? row.products : []
      const exists = products.some((product) => product.productKey === planKey)
      return { ...row, products: exists ? products.map((product) => (product.productKey === planKey ? { ...product, amount } : product)) : [...products, { productKey: planKey, amount }] }
    }))
    setDirty(true)
  }
  const setCurrentRowAmount = (rowKey, product, amount) => {
    const productKey = product?.productKey || coverageSimulationPlanKey(product)
    setRows((prev) => prev.map((row) => {
      if (row._simulationKey !== rowKey) return row
      const currentProducts = Array.isArray(row.currentProducts) ? row.currentProducts : []
      const exists = currentProducts.some((current) => current.productKey === productKey)
      const nextCurrentProducts = exists
        ? currentProducts.map((current) => (current.productKey === productKey ? { ...current, amount } : current))
        : [...currentProducts, { ...product, productKey, amount }]
      return {
        ...row,
        currentProducts: nextCurrentProducts,
        _beforeAmount: coverageSimulationCurrentRowTotal({ currentProducts: nextCurrentProducts }),
      }
    }))
    setDirty(true)
  }
  const addPlan = () => {
    const plan = createCoverageRemodelingPlan(plans.length)
    setPlans((prev) => [...prev, plan])
    setRows((prev) => prev.map((row) => ({ ...row, products: [...(Array.isArray(row.products) ? row.products : []), { ...plan, amount: 0 }] })))
    setDirty(true)
  }
  const removePlan = (planIndex) => {
    const plan = plans[planIndex]
    if (!plan || !window.confirm(`${plan.productName || '추천 플랜'}을 삭제할까요?`)) return
    setPlans((prev) => prev.filter((_, index) => index !== planIndex))
    setRows((prev) => prev.map((row) => ({ ...row, products: (Array.isArray(row.products) ? row.products : []).filter((product) => product.productKey !== plan.productKey) })))
    setDirty(true)
  }
  const reloadSaved = () => {
    if (busy || loading) return
    if (dirty && !window.confirm('저장하지 않은 변경사항은 사라집니다. 저장된 리모델링안을 불러올까요?')) return
    load({ showLoading: false, showNotice: true })
  }
  const clearSimulation = () => {
    if (!window.confirm('입력한 리모델링 제안안을 모두 초기화할까요?')) return
    const normalized = normalizeCoverageRemodelingSimulation({ ...report, remodelingRows: [] })
    setPlans([])
    setRows(normalized.rows.map((row) => ({ ...row, products: [] })))
    setDirty(true)
  }
  const save = useCallback(async ({ quiet = false } = {}) => {
    if (!report || busy) return null
    setBusy('save')
    setError('')
    try {
      const payload = buildCoverageRemodelingReport(report, plans, rows)
      const res = await api.put(`/customer-management/customers/${customerId}/coverage-summary`, { report: payload }, { __silent: true, __suppressErrorPageRedirect: true })
      const saved = unwrap(res)
      applyReport(saved)
      if (!quiet) setNotice('리모델링 시뮬레이션을 저장했습니다.')
      return saved
    } catch (e) {
      setError(getApiErrorMessage(e, '리모델링 시뮬레이션 저장에 실패했습니다.'))
      return null
    } finally {
      setBusy('')
    }
  }, [applyReport, busy, customerId, plans, report, rows])
  const downloadExcel = async () => {
    if (!report || busy) return
    const saved = await save({ quiet: true })
    if (!saved) return
    setBusy('excel')
    try {
      const res = await api.get(`/customer-management/customers/${customerId}/coverage-summary/excel`, { responseType: 'blob', __silent: true, __suppressErrorPageRedirect: true })
      const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filenameFromDisposition(res.headers, `${safeFilenamePart(customerName)}_보장요약리포트_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
      setNotice('치료단계·리모델링·비교 시트가 포함된 엑셀을 다운로드했습니다.')
    } catch (e) {
      setError(getApiErrorMessage(e, '보장요약 엑셀 다운로드에 실패했습니다.'))
    } finally {
      setBusy('')
    }
  }
  const goBack = () => {
    if (dirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 고객 화면으로 돌아갈까요?')) return
    navigate(`/support/customers/${customerId}#contracts`)
  }

  if (loading) return <main className="grid min-h-screen place-items-center bg-slate-50 text-sm text-slate-500">리모델링 시뮬레이션을 불러오는 중…</main>
  if (error && !report) return <main className="min-h-screen bg-slate-50 p-6"><div className="mx-auto max-w-3xl"><ErrorState title="리모델링 시뮬레이션을 열 수 없습니다" description={error} onRetry={load} /></div></main>

  return (
    <main data-testid="coverage-remodeling-simulation" className="rd-root">
      <style>{DASHBOARD_STYLES}</style>
      <header className="rd-topbar rd-no-print">
        <div className="rd-brand">
          <button type="button" className="rd-back" aria-label="고객 보험계약으로 돌아가기" title="고객 보험계약으로 돌아가기" onClick={goBack}><ArrowLeft /></button>
          <span className="rd-mark">EZ</span>
          <div><span>설계EZ 고객 보장 컨설팅</span><h1>{customerName} 리모델링 시뮬레이션</h1></div>
          {dirty ? <b className="rd-dirty">저장 필요</b> : null}
        </div>
        <div className="rd-slogan"><span>보험료는 정교하게</span><strong>보장은 충분하게</strong></div>
        <div className="rd-tools">
          <ToolbarButton tone="solid" onClick={() => save()} disabled={Boolean(busy) || !dirty}><Save />{busy === 'save' ? '저장 중…' : '저장'}</ToolbarButton>
          <ToolbarButton onClick={reloadSaved} disabled={Boolean(busy) || loading}><RotateCcw />{busy === 'load' ? '불러오는 중…' : '불러오기'}</ToolbarButton>
          <ToolbarButton tone="warn" onClick={clearSimulation} disabled={Boolean(busy) || (!plans.length && !dirty)}><Trash2 />전체 초기화</ToolbarButton>
          <ToolbarButton onClick={() => window.print()} disabled={Boolean(busy)}><Printer />PDF 인쇄</ToolbarButton>
          <ToolbarButton onClick={downloadExcel} disabled={Boolean(busy) || !report}><FileSpreadsheet />{busy === 'excel' ? '생성 중…' : '엑셀 다운로드'}</ToolbarButton>
        </div>
      </header>
      <nav className="rd-tabs rd-no-print" role="tablist" aria-label="리모델링 분석 화면">
        {[['analysis', '기계약 분석'], ['cancer', '암 치료단계별'], ['cardio', '뇌·심 치료단계별']].map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={activeTab === key} className={activeTab === key ? 'active' : ''} onClick={() => setActiveTab(key)}>{label}{key !== 'analysis' ? <span className="rd-tab-badge">자동</span> : null}</button>)}
      </nav>
      {notice ? <div className="rd-notice rd-no-print"><span>{notice}</span><button type="button" onClick={() => setNotice('')}>닫기</button></div> : null}
      {error ? <div className="rd-error rd-no-print">{error}</div> : null}

      {activeTab === 'analysis' ? (
        <section role="tabpanel" className="rd-content rd-analysis-panel">
          <CustomerBoard
            report={report}
            rows={rows}
            currentPremium={currentPremium}
            proposedPremium={proposedPremium}
            plans={plans}
            summaryCollapsed={summaryCollapsed}
            onToggleSummary={() => setSummaryCollapsed((value) => !value)}
          />
          <section className="rd-card rd-grid-card">
            <div className="rd-grid-head rd-no-print">
              <div><h2>담보 현황표</h2><span>기계약 {currentProducts.length}건 · 신규제안 {plans.length}건</span></div>
              <div className="rd-grid-actions">
                <label className="rd-search"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="보장 검색" /></label>
                <select value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)} aria-label="분류 선택"><option value="">전체 분류</option>{groups.map((group) => <option key={group} value={group}>{group}</option>)}</select>
                <label className="rd-changed"><input type="checkbox" checked={changedOnly} onChange={(event) => setChangedOnly(event.target.checked)} />변경 항목만</label>
                <button type="button" className={`rd-mini ${editCurrent ? 'primary' : ''}`} onClick={() => setEditCurrent((value) => !value)}>{editCurrent ? '기계약 수정 완료' : '기계약 수정'}</button>
                <button type="button" className="rd-mini primary" data-testid="add-remodeling-plan" onClick={addPlan}><Plus />계약 추가</button>
              </div>
            </div>
            <PortfolioTable rows={visibleRows} currentProducts={currentProducts} plans={plans} groupSpans={groupSpans} editCurrent={editCurrent} setPlanField={setPlanField} setCurrentRowAmount={setCurrentRowAmount} setRowAmount={setRowAmount} removePlan={removePlan} />
          </section>
        </section>
      ) : (
        <div role="tabpanel" className="rd-content rd-stage-panel"><TreatmentStageTable kind={activeTab} rows={rows} hasRemodelingPlans={plans.length > 0} customerName={customerName} /></div>
      )}
    </main>
  )
}

const DASHBOARD_STYLES = `
  @page { size: A3 landscape; margin: 8mm; }
  .rd-root{--bg:#edf1f6;--surface:#fff;--ink:#172033;--soft:#607087;--line:#dce3ec;--line-strong:#bdc8d6;--accent:#2563eb;--accent-soft:#eaf2ff;--gold:#92651a;--gold-bg:#fff8e8;min-height:100vh;background:var(--bg);color:var(--ink);font-family:"Noto Sans KR",Pretendard,system-ui,sans-serif;font-size:13px;line-height:1.4;letter-spacing:0}
  .rd-root *{box-sizing:border-box}.rd-root button,.rd-root input,.rd-root select{font:inherit;letter-spacing:0}.rd-topbar{position:sticky;top:0;z-index:70;display:flex;align-items:center;gap:18px;min-height:59px;padding:10px 20px;background:#162235;color:#fff;border-bottom:1px solid #27364a}.rd-brand{display:flex;align-items:center;gap:9px;min-width:320px}.rd-brand>div{min-width:0}.rd-brand span{display:block;color:#9eacbd;font-size:10px;font-weight:600}.rd-brand h1{margin:1px 0 0;max-width:390px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:15px;line-height:1.25}.rd-mark{padding:5px 8px!important;border-radius:6px;background:var(--accent);color:#fff!important;font-size:14px!important;font-weight:900!important}.rd-back{display:grid;width:30px;height:30px;place-items:center;border:1px solid rgba(255,255,255,.16);border-radius:6px;background:rgba(255,255,255,.06);color:#fff}.rd-back svg{width:16px}.rd-dirty{margin-left:3px;padding:3px 7px;border:1px solid #e7be66;border-radius:5px;background:#fff5d8;color:#7d5510;font-size:10px;white-space:nowrap}.rd-slogan{display:flex;gap:10px;margin:auto;padding:6px 13px;border:1px solid rgba(255,255,255,.1);border-radius:999px;background:rgba(255,255,255,.05);font-size:12px;font-weight:700;white-space:nowrap}.rd-slogan span{color:#b8c7dc}.rd-slogan strong{color:#91c1ff}.rd-tools{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:6px}.rd-tool-button{display:inline-flex;height:32px;align-items:center;gap:5px;padding:0 10px;border:1px solid rgba(255,255,255,.17);border-radius:6px;background:rgba(255,255,255,.07);color:#eaf0f7;font-size:11px;font-weight:600}.rd-tool-button:hover{background:rgba(255,255,255,.15)}.rd-tool-button:disabled{cursor:not-allowed;opacity:.45}.rd-tool-button svg{width:14px;height:14px}.rd-tool-button.solid{border-color:var(--accent);background:var(--accent)}.rd-tool-button.warn{color:#ffc8bd;border-color:rgba(232,118,92,.45)}
  .rd-tabs{position:sticky;top:59px;z-index:65;display:flex;gap:3px;padding:0 21px;background:#1c2b40;border-bottom:1px solid #2b3b50}.rd-tabs button{padding:11px 18px;border:0;border-bottom:2px solid transparent;background:transparent;color:#9eacbd;font-size:12px;font-weight:700}.rd-tabs button:hover{color:#dce6f3}.rd-tabs button.active{border-bottom-color:#60a5fa;color:#fff}.rd-tab-badge{margin-left:6px;padding:1px 5px;border-radius:7px;background:#2563eb;color:#fff;font-size:9px}.rd-content{padding:14px 20px 44px}.rd-notice,.rd-error{display:flex;align-items:center;justify-content:space-between;margin:10px 20px 0;padding:8px 11px;border-radius:6px;font-size:11px}.rd-notice{border:1px solid #a9dfca;background:#edf9f5;color:#216854}.rd-notice button{border:0;background:transparent;color:inherit;font-weight:800}.rd-error{border:1px solid #fecaca;background:#fff1f2;color:#be123c}
  .rd-card{border:1px solid var(--line);border-radius:8px;background:var(--surface);box-shadow:0 1px 2px rgba(23,32,51,.04),0 7px 22px rgba(23,32,51,.05)}.rd-board{display:grid;grid-template-columns:300px minmax(0,1fr);gap:14px;margin-bottom:13px}.rd-board.rd-board-collapsed{grid-template-columns:minmax(0,1fr)}.rd-customer{padding:15px 17px}.rd-eyebrow{color:var(--accent);font-size:10px;font-weight:800;letter-spacing:.08em}.rd-customer-grid{display:grid;grid-template-columns:1fr 1fr;gap:9px 11px;margin-top:11px}.rd-customer-grid label{min-width:0}.rd-customer-grid label.full{grid-column:1/-1}.rd-customer-grid span{display:block;margin-bottom:3px;color:var(--soft);font-size:10px;font-weight:600}.rd-customer-grid input{width:100%;height:31px;padding:0 8px;border:1px solid var(--line-strong);border-radius:5px;background:#fff;color:var(--ink);font-size:12px}.rd-summary{overflow:hidden;border:1px solid var(--line);border-radius:8px;background:#fff}.rd-summary-tools{display:flex;justify-content:flex-end;min-height:33px;padding:4px 5px;border-bottom:1px solid var(--line);background:#fff}.rd-summary-toggle{display:inline-flex;height:24px;align-items:center;padding:0 8px;border:1px solid #50a77e;border-radius:4px;background:#e9f7ef;color:#176541;font-size:10px;font-weight:700}.rd-summary-toggle:hover{border-color:#328b65;background:#d9f1e4;color:#125437}.rd-summary-collapsed{display:flex;align-items:center;justify-content:flex-end;min-height:43px;padding:5px;border:1px solid var(--line);border-radius:8px;background:#fff}.rd-summary-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:1px;background:var(--line)}.rd-metric{min-width:0;padding:11px 13px;background:#fff}.rd-metric.gold{background:var(--gold-bg)}.rd-metric.blue{background:#edf5ff}.rd-metric-label{display:flex;align-items:center;gap:5px;color:var(--soft);font-size:10px;font-weight:600}.rd-metric-heading{color:var(--ink);font-size:11px;font-weight:800}.rd-dot{width:7px;height:7px;border-radius:50%}.rd-metric-value{margin-top:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:18px;font-weight:800;font-variant-numeric:tabular-nums}.rd-metric-detail{margin-top:2px;color:var(--soft);font-size:10px}.rd-metric-list{display:grid;gap:3px;margin-top:5px}.rd-metric-row{display:flex;align-items:baseline;justify-content:space-between;gap:8px}.rd-metric-row span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--soft);font-size:10px}.rd-metric-row strong{flex:0 0 auto;font-size:11px;font-variant-numeric:tabular-nums}.rd-metric-row strong.is-empty{color:#b7c0cc;font-weight:500}
  .rd-grid-card{overflow:hidden}.rd-grid-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:10px 13px;border-bottom:1px solid var(--line)}.rd-grid-head>div:first-child{display:flex;align-items:baseline;gap:8px}.rd-grid-head h2{margin:0;font-size:14px}.rd-grid-head span{color:var(--soft);font-size:10px}.rd-grid-actions{display:flex;flex-wrap:wrap;align-items:center;gap:6px}.rd-search{position:relative}.rd-search svg{position:absolute;top:8px;left:8px;width:13px;height:13px;color:#8a98aa}.rd-search input{width:170px;padding-left:27px!important}.rd-grid-actions input,.rd-grid-actions select{height:30px;padding:0 8px;border:1px solid var(--line-strong);border-radius:5px;background:#fff;font-size:11px}.rd-changed{display:flex;align-items:center;gap:5px;height:30px;padding:0 8px;border:1px solid var(--line-strong);border-radius:5px;color:var(--soft);font-size:10px;font-weight:600}.rd-changed input{width:13px;height:13px}.rd-mini{display:inline-flex;height:30px;align-items:center;gap:4px;padding:0 9px;border:1px solid var(--line-strong);border-radius:5px;background:#fff;color:#315c96;font-size:10px;font-weight:700}.rd-mini.primary{border-color:#9fc2f8;background:#eef5ff;color:#1555b5}.rd-mini svg{width:13px}.rd-table-scroller{max-height:calc(100vh - 215px);overflow:auto}.rd-portfolio-table{width:max-content;min-width:100%;border-collapse:separate;border-spacing:0;font-size:11px}.rd-portfolio-table th,.rd-portfolio-table td{height:37px;padding:0;border-right:1px solid var(--line);border-bottom:1px solid var(--line);text-align:center;vertical-align:middle}.rd-portfolio-table thead th{position:sticky;top:0;z-index:30;min-height:142px;background:#f3f6f9}.rd-cat{width:112px;min-width:112px}.rd-name{width:224px;min-width:224px}.rd-sticky-cat{position:sticky!important;left:0;z-index:22}.rd-sticky-name{position:sticky!important;left:112px;z-index:21}.rd-portfolio-table thead .rd-sticky-cat,.rd-portfolio-table thead .rd-sticky-name{z-index:45;background:#e9eef4}.rd-category-band{padding:8px!important;color:#fff;font-size:14px;font-weight:800}.rd-coverage-name{padding:6px 10px!important;color:#273449;font-size:12px;font-weight:700}.rd-contract-column{width:172px;min-width:172px;vertical-align:top!important}.rd-new-column{background:#eef5ff!important}.rd-contract-head{display:grid;gap:4px;padding:7px;text-align:left}.rd-contract-head strong{font-size:11px}.rd-contract-badge{width:max-content;padding:1px 5px;border-radius:4px;background:#64748b;color:#fff;font-size:9px;font-weight:800}.rd-contract-badge.is-new{background:var(--accent)}.rd-product-name{height:30px;overflow:hidden;color:#253349;font-size:11px;font-weight:700;line-height:1.35}.rd-contract-meta{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--soft);font-size:9px}.rd-premium{color:#23334a;font-size:11px;font-weight:800;text-align:right}.rd-premium span{color:var(--soft);font-size:9px;font-weight:500}.rd-contract-head-line{display:flex;align-items:center;justify-content:space-between}.rd-delete{display:grid;width:22px;height:22px;place-items:center;border:0;border-radius:4px;background:transparent;color:#8d99a8}.rd-delete:hover{background:#feeceb;color:#b42318}.rd-delete svg{width:13px}.rd-proposed-head input{width:100%;height:25px;min-width:0;padding:0 6px;border:1px solid #bad0ed;border-radius:4px;background:#fff;color:#243248;font-size:10px}.rd-contract-input-row{display:grid;grid-template-columns:1fr 1fr;gap:3px}.rd-premium-input{display:flex;align-items:center;gap:3px}.rd-premium-input input{text-align:right}.rd-premium-input span{color:var(--soft);font-size:8px;white-space:nowrap}.rd-current-value{min-width:172px;padding:0 8px!important;background:#fff;color:#35445a;font-variant-numeric:tabular-nums;text-align:right!important}.rd-current-value.is-editing{padding:0!important;background:#fffdf3}.rd-current-value.is-modified{background:#fff4d6;box-shadow:inset 3px 0 0 #e5a62f}.rd-current-value.is-modified .rd-amount-input{background:#fff9e8}.rd-new-value{min-width:172px;background:#fbfdff}.rd-amount-input{width:100%;height:36px;padding:0 9px;border:0;background:transparent;color:#1f2d42;text-align:right;font-size:11px;font-variant-numeric:tabular-nums;outline:none}.rd-amount-input:focus{background:#edf5ff;box-shadow:inset 0 0 0 2px #60a5fa}.rd-total-column{width:130px;min-width:130px;padding:0 9px!important;font-weight:800;font-variant-numeric:tabular-nums}.rd-current-total{position:sticky!important;right:260px;z-index:19;background:#f7f9fb}.rd-proposed-total{position:sticky!important;right:130px;z-index:19;border-left:2px solid #c39131!important;background:var(--gold-bg);color:var(--gold)}.rd-difference-column{width:130px;min-width:130px;padding:0 9px!important;font-weight:800;font-variant-numeric:tabular-nums}.rd-difference{position:sticky!important;right:0;z-index:19;background:#fff}.rd-portfolio-table thead .rd-current-total,.rd-portfolio-table thead .rd-proposed-total,.rd-portfolio-table thead .rd-difference{z-index:45}.rd-difference.up{color:#168061}.rd-difference.down{color:#c24132}.rd-group-ending td{border-bottom:3px double #8e99a7}.rd-group-ending .rd-category-band{border-bottom:3px double rgba(255,255,255,.65)}
  .rd-stage-panel{max-width:1660px;margin:0 auto}.rd-stage-card{overflow:hidden}.rd-stage-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 17px;border-bottom:1px solid var(--line)}.rd-stage-head h2{margin:0;font-size:15px}.rd-stage-head p{margin:3px 0 0;color:var(--soft);font-size:10px}.rd-linked-legend{display:flex;align-items:center;gap:6px;color:var(--soft);font-size:10px}.rd-linked-legend span{width:12px;height:12px;border:1px solid #a9d8c7;border-radius:2px;background:#e6f5ef}.rd-stage-scroller{max-height:calc(100vh - 190px);overflow:auto}.rd-stage-table{width:max-content;min-width:100%;border-collapse:separate;border-spacing:0}.rd-stage-table th,.rd-stage-table td{height:38px;border-right:1px solid var(--line);border-bottom:1px solid var(--line);text-align:center}.rd-stage-table thead th{position:sticky;top:0;z-index:20;padding:7px;background:#f1f4f8;font-size:10px}.rd-stage-table thead tr:nth-child(2) th{top:33px}.rd-stage-row-name{position:sticky;left:0;z-index:15;width:210px;min-width:210px;padding:0 11px!important;background:#fff;text-align:left!important;font-size:11px}.rd-stage-table thead .rd-stage-row-name{z-index:30;background:#e9eef4;text-align:center!important}.rd-stage-group.diagnosis,.rd-stage-column.diagnosis{background:#e7f2ee!important;color:#246b58}.rd-stage-group.surgery,.rd-stage-column.surgery{background:#e8f0f8!important;color:#315f88}.rd-stage-group.drug,.rd-stage-column.drug,.rd-stage-group.intensive,.rd-stage-column.intensive{background:#f8eee9!important;color:#a55338}.rd-stage-group.radiation,.rd-stage-column.radiation,.rd-stage-group.thrombus,.rd-stage-column.thrombus{background:#f1ebf7!important;color:#76508d}.rd-stage-column{min-width:112px;width:112px}.rd-stage-value{min-width:112px;width:112px;padding:4px 7px;background:#fff;text-align:right!important;font-variant-numeric:tabular-nums}.rd-stage-value.linked{background:#e8f5f0}.rd-stage-value strong{display:block;color:#246b58;font-size:11px}.rd-stage-value span{display:block;margin-top:1px;color:#849285;font-size:8px}.rd-stage-table tfoot td{position:sticky;bottom:0;z-index:16;padding:8px;background:var(--gold-bg);color:var(--gold);font-size:11px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums}.rd-stage-table tfoot .rd-stage-row-name{z-index:25;background:#f5ead0;text-align:left!important}.rd-stage-note{padding:10px 16px;border-top:1px solid var(--line);background:#fafbfd;color:var(--soft);font-size:10px}
  @media(max-width:1050px){.rd-topbar{position:relative;align-items:flex-start}.rd-tabs{top:0}.rd-slogan{display:none}.rd-tools{margin-left:auto}.rd-board{grid-template-columns:1fr}.rd-summary-grid{grid-template-columns:repeat(3,minmax(160px,1fr))}.rd-content{padding:12px}.rd-current-total{right:240px}.rd-proposed-total{right:120px}.rd-total-column,.rd-difference-column{width:120px;min-width:120px}}
  @media(max-width:700px){.rd-topbar{padding:10px}.rd-brand{min-width:0;width:100%}.rd-brand h1{max-width:230px}.rd-tools{justify-content:flex-start}.rd-tabs{overflow:auto;padding:0 8px}.rd-tabs button{padding:10px 12px;white-space:nowrap}.rd-summary-grid{grid-template-columns:repeat(2,minmax(145px,1fr))}.rd-grid-head{align-items:flex-start;flex-direction:column}.rd-grid-actions{width:100%}.rd-search input{width:145px}.rd-cat{width:82px;min-width:82px}.rd-name{width:190px;min-width:190px}.rd-sticky-name{left:82px!important}.rd-current-total,.rd-proposed-total,.rd-difference{position:static!important}}
  @media print{body{background:#fff!important}.rd-no-print{display:none!important}.rd-root{background:#fff}.rd-content{padding:0}.rd-card{border:0;box-shadow:none}.rd-board{display:none}.rd-table-scroller,.rd-stage-scroller{max-height:none;overflow:visible}.rd-portfolio-table,.rd-stage-table{width:100%;min-width:0;font-size:7pt}.rd-portfolio-table th,.rd-portfolio-table td,.rd-stage-table th,.rd-stage-table td{position:static!important}.rd-amount-input,.rd-proposed-head input{border:0!important;padding:0!important;font-size:7pt!important}.rd-delete{display:none}.rd-stage-note{font-size:7pt}}
`
